import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import {
    ArrowBack,
    CheckCircleOutline,
    CloudUpload,
    DeleteForever,
    ErrorOutline,
    ExpandMore,
    InsertDriveFile,
    LocalShipping,
    Visibility,
    Warning,
} from '@material-ui/icons'

import { Container } from '../../components/Container'
import { DropFiles } from '../../components/DropFiles'
import {
    FurnitureInstallVendor,
    useFurnitureInstallUpload,
} from '../../hooks/useFurnitureInstallUpload'

const VENDOR_OPTIONS: {
    value: FurnitureInstallVendor
    label: string
    description: string
    formats: string
}[] = [
    {
        value: 'D12',
        label: 'D12',
        description: 'Level sheets or unit check sheets',
        formats: '.xlsx',
    },
    {
        value: 'ISL',
        label: 'ISL',
        description: 'Per-bed area with bedroom & living sections',
        formats: '.xlsx, .csv',
    },
    {
        value: 'SW',
        label: 'SW',
        description: 'Room inventory list with item columns',
        formats: '.xlsx',
    },
]

export const FurnitureInstallUpload = () => {
    const history = useHistory()
    const theme = useTheme()
    const [vendorFormat, setVendorFormat] = useState<FurnitureInstallVendor>(
        'D12',
    )
    const [inspectionName, setInspectionName] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [warningsOpen, setWarningsOpen] = useState(false)
    const {
        upload,
        result,
        errors,
        isUploading,
        reset,
    } = useFurnitureInstallUpload()

    const handleFileChange = (files: File[]) => {
        reset()
        setFile(files[0] || null)
    }

    const handleUpload = async () => {
        if (!file || !inspectionName.trim()) {
            toast.error('Choose a spreadsheet and enter an inspection name')
            return
        }
        try {
            await upload({ file, vendorFormat, inspectionName })
            toast.success('Furniture install inspection created')
        } catch {
            // Errors rendered in-page below.
        }
    }

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const canSubmit = !!file && !!inspectionName.trim() && !isUploading

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                height: 'calc(100vh - 104px)',
                overflowY: 'auto',
                background: theme.palette.background.default,
            }}
        >
            <Box
                style={{
                    maxWidth: 720,
                    width: '100%',
                    margin: '0 auto',
                    padding: theme.spacing(4),
                }}
            >
                {/* Back link */}
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => history.goBack()}
                    style={{
                        textTransform: 'none',
                        marginBottom: theme.spacing(2),
                        color: theme.palette.text.secondary,
                    }}
                >
                    Back to Inspections
                </Button>

                {/* Header */}
                <Paper
                    elevation={0}
                    style={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        borderRadius: 12,
                        padding: theme.spacing(3, 4),
                        marginBottom: theme.spacing(3),
                        color: 'white',
                    }}
                >
                    <Container style={{ alignItems: 'center' }}>
                        <LocalShipping
                            style={{
                                fontSize: 36,
                                marginRight: theme.spacing(2),
                            }}
                        />
                        <Box>
                            <Typography
                                variant="h5"
                                style={{ fontWeight: 700, color: 'white' }}
                            >
                                Furniture Install Upload
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{
                                    color: 'rgba(255,255,255,0.85)',
                                    marginTop: 2,
                                }}
                            >
                                Upload a vendor install sheet to create an
                                inspection with furniture checks
                            </Typography>
                        </Box>
                    </Container>
                </Paper>

                {/* Success result */}
                {result && (
                    <Paper
                        elevation={0}
                        style={{
                            borderRadius: 12,
                            border: `2px solid ${theme.palette.success.main}`,
                            overflow: 'hidden',
                            marginBottom: theme.spacing(3),
                        }}
                    >
                        <Box
                            style={{
                                background: theme.palette.success.main,
                                padding: theme.spacing(2, 3),
                            }}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <CheckCircleOutline
                                    style={{
                                        color: 'white',
                                        fontSize: 28,
                                        marginRight: theme.spacing(1.5),
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    style={{ fontWeight: 600, color: 'white' }}
                                >
                                    Inspection Created Successfully
                                </Typography>
                            </Container>
                        </Box>
                        <Box style={{ padding: theme.spacing(3) }}>
                            <Box
                                display="flex"
                                style={{
                                    gap: theme.spacing(2),
                                    flexWrap: 'wrap',
                                }}
                            >
                                {[
                                    {
                                        label: 'Units Matched',
                                        value: result.units_matched,
                                    },
                                    {
                                        label: 'Areas Assigned',
                                        value: result.area_inspections_created,
                                    },
                                    {
                                        label: 'Furniture Checks',
                                        value:
                                            result.inventory_inspections_created,
                                    },
                                    {
                                        label: 'New Inventory Types',
                                        value: result.inventory_configs_created,
                                    },
                                ].map((stat) => (
                                    <Paper
                                        key={stat.label}
                                        elevation={0}
                                        style={{
                                            flex: '1 1 140px',
                                            padding: theme.spacing(2),
                                            textAlign: 'center',
                                            borderRadius: 8,
                                            background: theme.palette.grey[50],
                                            border: `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <Typography
                                            variant="h4"
                                            style={{
                                                fontWeight: 700,
                                                color:
                                                    theme.palette.primary.main,
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>

                            {result.warnings.length > 0 && (
                                <Box style={{ marginTop: theme.spacing(2) }}>
                                    <ButtonBase
                                        onClick={() =>
                                            setWarningsOpen(!warningsOpen)
                                        }
                                        style={{
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            padding: theme.spacing(1, 1.5),
                                            borderRadius: 6,
                                            background: '#fff3e0',
                                            border: '1px solid #ffe0b2',
                                        }}
                                    >
                                        <Container
                                            style={{ alignItems: 'center' }}
                                        >
                                            <Warning
                                                style={{
                                                    fontSize: 18,
                                                    color: '#e65100',
                                                    marginRight: theme.spacing(
                                                        1,
                                                    ),
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    color: '#e65100',
                                                }}
                                            >
                                                {result.warnings.length} warning
                                                {result.warnings.length !== 1
                                                    ? 's'
                                                    : ''}
                                            </Typography>
                                        </Container>
                                        <ExpandMore
                                            style={{
                                                color: '#e65100',
                                                transform: warningsOpen
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                            }}
                                        />
                                    </ButtonBase>
                                    <Collapse in={warningsOpen}>
                                        <Box
                                            style={{
                                                padding: theme.spacing(
                                                    1.5,
                                                    1.5,
                                                    0,
                                                ),
                                            }}
                                        >
                                            {result.warnings.map((w, i) => (
                                                <Typography
                                                    key={i}
                                                    variant="body2"
                                                    style={{
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    • {w}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}

                            <Box
                                style={{
                                    marginTop: theme.spacing(3),
                                    display: 'flex',
                                    gap: theme.spacing(2),
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Visibility />}
                                    onClick={() =>
                                        history.push(
                                            `/inspect/${result.inspection_id}`,
                                        )
                                    }
                                    style={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 8,
                                        padding: theme.spacing(1, 3),
                                    }}
                                >
                                    Open Inspection
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        reset()
                                        setFile(null)
                                        setInspectionName('')
                                    }}
                                    style={{
                                        textTransform: 'none',
                                        borderRadius: 8,
                                        padding: theme.spacing(1, 3),
                                    }}
                                >
                                    Upload Another
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Error card */}
                {errors.length > 0 && (
                    <Paper
                        elevation={0}
                        style={{
                            borderRadius: 12,
                            border: `2px solid ${theme.palette.error.main}`,
                            overflow: 'hidden',
                            marginBottom: theme.spacing(3),
                        }}
                    >
                        <Box
                            style={{
                                background: theme.palette.error.main,
                                padding: theme.spacing(1.5, 3),
                            }}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <ErrorOutline
                                    style={{
                                        color: 'white',
                                        fontSize: 22,
                                        marginRight: theme.spacing(1),
                                    }}
                                />
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 600, color: 'white' }}
                                >
                                    Upload could not be completed
                                </Typography>
                            </Container>
                        </Box>
                        <Box
                            style={{
                                padding: theme.spacing(2, 3),
                                maxHeight: 300,
                                overflowY: 'auto',
                            }}
                        >
                            {errors.map((error, i) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    style={{
                                        color: theme.palette.error.dark,
                                        padding: theme.spacing(0.5, 0),
                                    }}
                                >
                                    {error}
                                </Typography>
                            ))}
                        </Box>
                    </Paper>
                )}

                {/* Upload form */}
                {!result && (
                    <Paper
                        elevation={0}
                        style={{
                            borderRadius: 12,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            overflow: 'hidden',
                        }}
                    >
                        <Box style={{ padding: theme.spacing(3, 4) }}>
                            {/* Vendor selector */}
                            <Typography
                                variant="subtitle1"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: theme.spacing(1.5),
                                }}
                            >
                                1. Select Vendor Format
                            </Typography>
                            <Box
                                display="flex"
                                style={{
                                    gap: theme.spacing(1.5),
                                    marginBottom: theme.spacing(3),
                                }}
                            >
                                {VENDOR_OPTIONS.map((opt) => {
                                    const selected = vendorFormat === opt.value
                                    return (
                                        <ButtonBase
                                            key={opt.value}
                                            onClick={() => {
                                                reset()
                                                setVendorFormat(opt.value)
                                                setFile(null)
                                            }}
                                            disabled={isUploading}
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                padding: theme.spacing(2),
                                                borderRadius: 10,
                                                border: `2px solid ${
                                                    selected
                                                        ? theme.palette.primary
                                                              .main
                                                        : theme.palette
                                                              .grey[300]
                                                }`,
                                                background: selected
                                                    ? theme.palette.primary
                                                          .main + '08'
                                                    : 'white',
                                                transition: 'all 0.15s ease',
                                            }}
                                        >
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    marginBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 700,
                                                        color: selected
                                                            ? theme.palette
                                                                  .primary.main
                                                            : theme.palette.text
                                                                  .primary,
                                                    }}
                                                >
                                                    {opt.label}
                                                </Typography>
                                                {selected && (
                                                    <CheckCircleOutline
                                                        style={{
                                                            marginLeft: 'auto',
                                                            fontSize: 20,
                                                            color:
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                        }}
                                                    />
                                                )}
                                            </Container>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                    textAlign: 'left',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {opt.description}
                                            </Typography>
                                            <Chip
                                                label={opt.formats}
                                                size="small"
                                                style={{
                                                    marginTop: theme.spacing(1),
                                                    fontSize: 11,
                                                    height: 22,
                                                    background:
                                                        theme.palette.grey[100],
                                                }}
                                            />
                                        </ButtonBase>
                                    )
                                })}
                            </Box>

                            {/* Inspection name */}
                            <Typography
                                variant="subtitle1"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: theme.spacing(1),
                                }}
                            >
                                2. Inspection Name
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="e.g. Mark Ft Collins Install - June 2026"
                                value={inspectionName}
                                inputProps={{ maxLength: 30 }}
                                onChange={(e) => {
                                    reset()
                                    setInspectionName(e.target.value)
                                }}
                                disabled={isUploading}
                                InputProps={{ style: { borderRadius: 8 } }}
                                style={{ marginBottom: theme.spacing(3) }}
                            />

                            {/* File drop */}
                            <Typography
                                variant="subtitle1"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: theme.spacing(1),
                                }}
                            >
                                3. Upload Spreadsheet
                            </Typography>

                            {!file ? (
                                <DropFiles
                                    onDrop={handleFileChange}
                                    multipleFiles={false}
                                    displayString={`Drop a ${vendorFormat} spreadsheet here or click to browse`}
                                    fileTypes={
                                        vendorFormat === 'ISL'
                                            ? {
                                                  'text/csv': ['.csv'],
                                                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                                                      '.xlsx',
                                                  ],
                                              }
                                            : {
                                                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                                                      '.xlsx',
                                                  ],
                                              }
                                    }
                                />
                            ) : (
                                <Container
                                    style={{
                                        border: `2px solid ${theme.palette.success.main}`,
                                        borderRadius: 8,
                                        padding: theme.spacing(1.5, 2),
                                        alignItems: 'center',
                                        background:
                                            theme.palette.success.main + '08',
                                    }}
                                >
                                    <InsertDriveFile
                                        style={{
                                            color: theme.palette.success.main,
                                            marginRight: theme.spacing(1.5),
                                        }}
                                    />
                                    <Box style={{ flex: 1 }}>
                                        <Typography
                                            variant="body1"
                                            style={{
                                                fontWeight: 500,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {file.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            {formatSize(file.size)}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            reset()
                                            setFile(null)
                                        }}
                                        disabled={isUploading}
                                        style={{
                                            color: theme.palette.error.main,
                                        }}
                                    >
                                        <DeleteForever />
                                    </IconButton>
                                </Container>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box
                            style={{
                                borderTop: `1px solid ${theme.palette.divider}`,
                                padding: theme.spacing(2, 4),
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                background: theme.palette.grey[50],
                            }}
                        >
                            {isUploading && (
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        marginRight: 'auto',
                                    }}
                                >
                                    <CircularProgress
                                        size={20}
                                        style={{
                                            marginRight: theme.spacing(1.5),
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        Processing spreadsheet...
                                    </Typography>
                                </Container>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CloudUpload />}
                                disabled={!canSubmit}
                                onClick={handleUpload}
                                style={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    padding: theme.spacing(1, 3),
                                    boxShadow: canSubmit
                                        ? '0 4px 12px rgba(0, 140, 133, 0.3)'
                                        : 'none',
                                }}
                            >
                                Create Inspection
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Container>
    )
}
